/* Per-(source,event) presentation: turns a notification into an icon + message + in-app route */
import { formatISOAsTimeAgo } from 'src/utils/date-time-utilities';
import { ACCESS_MANAGEMENT_SOURCE, AccessRequestNotification, parseAccessRequestNotification, XptNotification } from './notificationModels';

/**
 * Semantic weight of a notification, declared by the presenter.
 *
 * Kept separate from `iconName` so a UI layer can pick its own iconography from
 * intent instead of reverse-engineering a Cloudscape icon name.
 */
export type NotificationSeverity = 'success' | 'critical' | 'pending' | 'unknown';

export interface NotificationPresentation {
  iconName: string; // Cloudscape icon name
  severity: NotificationSeverity;
  /** Short scannable summary, e.g. "Access request approved". Sentence case, no trailing period. */
  title: string;
  /** Full detail line, e.g. who requested what for which business group. */
  message: string;
  route: string | null; // in-app SPA route, or null when the item is not click-through
  /** Label for the row's action, e.g. "Review request". Null when there is nowhere to go. */
  actionLabel: string | null;
  /**
   * How long ago the request was submitted, e.g. "5 minutes ago".
   *
   * Empty when the payload carries no timestamp — the Notification type has none of its own, so
   * this depends on the publisher forwarding the request's created_at.
   */
  timeAgo: string;
}

/** `owns` decides whether this presenter handles the notification
 * `present` renders it (only called when `owns` returned true).
 */
interface NotificationPresenter {
  owns: (notification: XptNotification) => boolean;
  present: (notification: XptNotification) => NotificationPresentation;
}

/** Fallback for notifications no presenter owns, or whose payload failed to parse. We still
 * show the item but with a "failed to load" message and no route, so the user knows it's broken/
 * The console.error in the payload parser carries the real diagnostics. */
const genericPresentation = (): NotificationPresentation => ({
  iconName: 'status-warning',
  severity: 'unknown',
  title: 'Notification unavailable',
  message: 'This notification could not be loaded',
  route: null,
  actionLabel: null,
  timeAgo: ''
});

// ── Access-management presenter ──────────────────────────────────────────────────────────────────

const nameAlias = (name: string, alias: string): string => `${name} (${alias})`;

/** The effective outcome of a request, or null while still pending. Comes from the item's own `kind`
 * (requester/beneficiary standalone outcome items) OR from `resolution_action` on a reconciled-in-place
 * SUBMITTED item (the FAs' item, whose `kind` stays SUBMITTED). */
const resolutionOf = (req: AccessRequestNotification): 'APPROVED' | 'REJECTED' | 'CANCELLED' | null => {
  if (req.kind !== 'SUBMITTED') {
    return req.kind;
  }
  return req.resolution_action ?? null;
};

const accessRequestMessage = (req: AccessRequestNotification): string => {
  const requester = nameAlias(req.requester_name, req.requester_alias);
  const beneficiary = nameAlias(req.beneficiary_name, req.beneficiary_alias);
  const isSelf = req.requester_alias === req.beneficiary_alias;
  const role = req.requested_role;
  const bg = req.business_group_name;
  const resolution = resolutionOf(req);

  // Still pending (a SUBMITTED item with no resolution yet): describe who requested what.
  if (!resolution) {
    return isSelf ? `${requester} requested ${role} for ${bg}` : `${requester} requested ${role} for ${bg} on behalf of ${beneficiary}`;
  }

  const isPartial = resolution === 'APPROVED' && req.is_partial_approval;
  const verb = isPartial ? 'approved with changes' : { APPROVED: 'approved', REJECTED: 'rejected', CANCELLED: 'cancelled' }[resolution];
  const by = req.resolved_by ? ` by ${req.resolved_by_name ? nameAlias(req.resolved_by_name, req.resolved_by) : req.resolved_by}` : '';
  return `${beneficiary}'s ${role} request for ${bg} was ${verb}${by}`;
};

/** Icon reflecting the request's outcome: approved -> tick, rejected/cancelled -> cross, still
 * pending -> pending clock. */
const iconForResolution = (resolution: 'APPROVED' | 'REJECTED' | 'CANCELLED' | null): string => {
  switch (resolution) {
    case 'APPROVED':
      return 'status-positive';
    case 'REJECTED':
    case 'CANCELLED':
      return 'status-negative';
    default:
      return 'status-pending';
  }
};

/**
 * Short summary line for the notification row. The detailed who/what/where stays in
 * `accessRequestMessage` — this is the scannable heading above it.
 */
const accessRequestTitle = (req: AccessRequestNotification): string => {
  const resolution = resolutionOf(req);
  if (!resolution) {
    // A SUBMITTED item with no resolution is the reviewer's "needs your review" item.
    return 'Access request needs review';
  }
  if (resolution === 'APPROVED') {
    return req.is_partial_approval ? 'Access request approved with changes' : 'Access request approved';
  }
  return resolution === 'REJECTED' ? 'Access request rejected' : 'Access request cancelled';
};

/**
 * Label for the row's single action.
 *
 * A pending request is an ask of the reader, so it says what they are being asked to do;
 * a resolved one is informational, so it just offers the record.
 */
const accessRequestActionLabel = (req: AccessRequestNotification): string =>
  resolutionOf(req) ? 'View request' : 'Review request';

/** Semantic counterpart of `iconForResolution`, for UI layers that pick their own icons. */
const severityForResolution = (resolution: 'APPROVED' | 'REJECTED' | 'CANCELLED' | null): NotificationSeverity => {
  switch (resolution) {
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
    case 'CANCELLED':
      return 'critical';
    default:
      return 'pending';
  }
};

const accessRequestPresenter: NotificationPresenter = {
  owns: (n) => n.source === ACCESS_MANAGEMENT_SOURCE,
  present: (n) => {
    const req = parseAccessRequestNotification(n);
    if (!req) {
      return genericPresentation();
    }
    // SUBMITTED item is the reviewer's "pending your review" notification -> the Finance Admin pending-requests console
    // APPROVED/REJECTED/CANCELLED are the requester/beneficiary's outcome -> their own requests page.
    const route = req.kind === 'SUBMITTED' ? '/admin-console/access-management' : '/my-requests';
    const resolution = resolutionOf(req);
    return {
      iconName: iconForResolution(resolution),
      severity: severityForResolution(resolution),
      title: accessRequestTitle(req),
      message: accessRequestMessage(req),
      route,
      actionLabel: accessRequestActionLabel(req),
      timeAgo: formatISOAsTimeAgo(req.created_at)
    };
  }
};

// ── Registry ─────────────────────────────────────────────────────────────────────────────────────

const PRESENTERS: NotificationPresenter[] = [accessRequestPresenter];

/** Build the presentation for a notification, delegating to the presenter that owns its type. */
export const getNotificationPresentation = (notification: XptNotification): NotificationPresentation => {
  const presenter = PRESENTERS.find((p) => p.owns(notification));
  return presenter ? presenter.present(notification) : genericPresentation();
};
